import React, { useState, useRef, useEffect } from 'react';
import { useAuth, API } from '../App';
import { hasFullAccess } from '../lib/platform';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageCircle, X, Send, Loader2, 
  Upload, Minimize2, Maximize2, FileText, Trash2, Mic, MicOff,
  Calculator, Banknote, Search, CloudSun, Tag, Home, Zap,
  ChevronDown, ChevronUp, MapPin, ExternalLink
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import AIConsentModal from './AIConsentModal';
import { hasAIConsent } from '../lib/aiConsent';

const FloatingAIChat = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [isListening, setIsListening] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  const [needsConsent, setNeedsConsent] = useState(false);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const recognitionRef = useRef(null);

  // Only show for Club/Expert members
  const isClub = hasFullAccess(user?.subscription_tier);

  // Hide on AI Agent page (same chat exists there)
  const isOnAgentPage = location.pathname === '/ai-agent';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = 'fr-FR';

      let finalTranscript = '';

      recognitionRef.current.onresult = (event) => {
        let interim = '';
        finalTranscript = '';
        for (let i = 0; i < event.results.length; i++) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interim += event.results[i][0].transcript;
          }
        }
        setInputValue(finalTranscript || interim);
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
        // Auto-send if we have a final transcript
        if (finalTranscript.trim()) {
          setTimeout(() => {
            const sendBtn = document.querySelector('[data-testid="floating-send-btn"]');
            if (sendBtn && !sendBtn.disabled) sendBtn.click();
          }, 300);
        }
      };

      recognitionRef.current.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'not-allowed') {
          toast.error('Autorise le micro dans ton navigateur');
        } else if (event.error === 'network') {
          toast.error('Erreur reseau - verifie ta connexion');
        } else if (event.error === 'no-speech') {
          toast.info('Aucune voix detectee, reessaie');
        }
      };
    }
  }, []);
  
  // Return null after all hooks
  if (!isClub || isOnAgentPage) return null;

  const toggleListening = () => {
    if (!recognitionRef.current) {
      toast.error('La reconnaissance vocale n\'est pas supportée par ton navigateur');
      return;
    }

    if (isListening) {
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      setInputValue('');
      recognitionRef.current.start();
      setIsListening(true);
    }
  };

  const handleSend = async () => {
    if (!inputValue.trim() || loading) return;

    // Check for navigation commands
    const navCommands = {
      'ouvre le calculateur': '/calculator',
      'ouvrir le calculateur': '/calculator',
      'va au calculateur': '/calculator',
      'ouvre le mdb': '/mdb',
      'ouvrir le mdb': '/mdb',
      'ouvre l\'outil mdb': '/mdb',
      'va au mdb': '/mdb',
      'ouvre le cadastre': '/cadastre',
      'ouvrir le cadastre': '/cadastre',
      'va au cadastre': '/cadastre',
      'ouvre le diagnostic': '/diagnostic-climatique',
      'ouvrir le diagnostic': '/diagnostic-climatique',
      'ouvre la communauté': '/community',
      'ouvrir la communauté': '/community',
      'va à la communauté': '/community',
      'ouvre les messages': '/messages',
      'ouvrir les messages': '/messages',
      'va aux messages': '/messages',
      'ouvre le dashboard': '/dashboard',
      'ouvrir le dashboard': '/dashboard',
      'va au dashboard': '/dashboard',
      'ouvre la classroom': '/classroom',
      'ouvrir la classroom': '/classroom',
      'va à la classroom': '/classroom',
      'ouvre l\'annuaire': '/directory',
      'ouvrir l\'annuaire': '/directory',
      'ouvre les partenaires': '/pros-directory',
      'ouvrir les partenaires': '/pros-directory',
    };

    const lowerInput = inputValue.toLowerCase().trim();
    for (const [command, path] of Object.entries(navCommands)) {
      if (lowerInput.includes(command)) {
        toast.success(`Navigation vers ${path}`);
        window.location.href = path;
        setInputValue('');
        return;
      }
    }

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date().toISOString()
    };
    
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/ai-skills/chat`,
        { message: userMessage.content, conversation_id: conversationId },
        { withCredentials: true }
      );

      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, response.data.message]);
    } catch (error) {
      console.error('Chat error:', error);
      toast.error(error.response?.data?.detail?.message || 'Erreur lors de l\'envoi');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Seuls les fichiers PDF sont acceptés');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);
    if (conversationId) {
      formData.append('conversation_id', conversationId);
    }

    setMessages(prev => [...prev, {
      role: 'user',
      content: `📄 ${file.name}`,
      timestamp: new Date().toISOString(),
      has_pdf: true
    }]);

    try {
      const response = await axios.post(
        `${API}/api/ai-chat/upload-pdf`,
        formData,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );

      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, response.data.message]);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const clearChat = () => {
    setMessages([]);
    setConversationId(null);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const toggleCard = (idx) => setExpandedCards(prev => ({ ...prev, [idx]: !prev[idx] }));

  const saveToPortfolio = async (skillData) => {
    try {
      await axios.post(`${API}/portfolio/save`, {
        bien: skillData.bien,
        loyer_estime: skillData.loyer_estime,
        rentabilite: skillData.rentabilite,
        credit_25ans: skillData.credit_25ans,
        verdict: skillData.verdict,
      }, { withCredentials: true });
      toast.success('Sauvegardé dans ton portfolio !');
    } catch (error) {
      toast.error(error.response?.status === 403 ? 'Réservé aux membres Club' : 'Erreur');
    }
  };

  const analyzeProperty = async (bien, index) => {
    if (loading) return;
    const msg = `Analyse la rentabilité du bien n°${index + 1} : "${bien.titre}" à ${bien.prix?.toLocaleString()}€, ${bien.surface || '?'}m², ${bien.localisation || ''}${bien.url ? ', url: ' + bien.url : ''}`;
    const userMessage = { role: 'user', content: msg, timestamp: new Date().toISOString() };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/ai-skills/chat`,
        { message: msg, conversation_id: conversationId },
        { withCredentials: true }
      );
      setConversationId(response.data.conversation_id);
      setMessages(prev => [...prev, response.data.message]);
    } catch (error) {
      toast.error(error.response?.data?.detail?.message || 'Erreur lors de l\'analyse');
      setMessages(prev => prev.slice(0, -1));
    } finally {
      setLoading(false);
    }
  };

  const skillIcons = {
    calculer_rentabilite: <Calculator className="h-3 w-3" />,
    simuler_credit: <Banknote className="h-3 w-3" />,
    rechercher_dvf: <Search className="h-3 w-3" />,
    diagnostic_climatique: <CloudSun className="h-3 w-3" />,
    estimer_prix: <Tag className="h-3 w-3" />,
    rechercher_biens: <Home className="h-3 w-3" />,
    analyser_bien: <Zap className="h-3 w-3" />,
  };

  const renderMiniSkillCard = (msg, idx) => {
    if (!msg.skill_data || !msg.skill_used) return null;
    const data = msg.skill_data;
    const open = expandedCards[idx];

    if (msg.skill_used === 'calculer_rentabilite') {
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Brut</div>
              <div className="font-bold text-[#0D47A1]">{data.rendement_brut?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Net</div>
              <div className="font-bold text-[#00B050]">{data.rendement_net?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Cash-flow</div>
              <div className={`font-bold ${data.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {data.cash_flow_mensuel >= 0 ? '+' : ''}{data.cash_flow_mensuel?.toLocaleString()}€/m
              </div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Mensualité</div>
              <div className="font-bold text-slate-700">{data.mensualite?.toLocaleString()}€</div>
            </div>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'simuler_credit') {
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="grid grid-cols-2 gap-1.5">
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Mensualité</div>
              <div className="font-bold text-[#0D47A1]">{data.mensualite?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400 uppercase">Coût intérêts</div>
              <div className="font-bold text-red-500">{data.cout_interets?.toLocaleString()}€</div>
            </div>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'rechercher_dvf') {
      const stats = data.stats || {};
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="grid grid-cols-3 gap-1.5">
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Transactions</div>
              <div className="font-bold">{stats.nb_transactions}</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Prix moy.</div>
              <div className="font-bold text-[#0D47A1]">{stats.prix_m2_moyen?.toLocaleString()}€/m²</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Médian</div>
              <div className="font-bold">{stats.prix_median?.toLocaleString()}€</div>
            </div>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'estimer_prix') {
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="grid grid-cols-3 gap-1.5">
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Basse</div>
              <div className="font-bold text-slate-600">{data.estimation_basse?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100 ring-1 ring-emerald-200">
              <div className="text-[10px] text-slate-400">Moyenne</div>
              <div className="font-bold text-emerald-600">{data.estimation_moyenne?.toLocaleString()}€</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Haute</div>
              <div className="font-bold text-slate-600">{data.estimation_haute?.toLocaleString()}€</div>
            </div>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'diagnostic_climatique') {
      const impact = data.impact_investissement || {};
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="flex items-center justify-between">
            <span className="text-slate-500">{data.zone_climatique} - {data.temperature?.mean_annual}°C</span>
            <Badge variant="outline" className={`text-[10px] ${impact.niveau_risque === 'élevé' ? 'border-red-300 text-red-600' : 'border-green-300 text-green-600'}`}>
              Risque {impact.niveau_risque}
            </Badge>
          </div>
        </div>
      );
    }

    if (msg.skill_used === 'analyser_bien') {
      const bien = data.bien || {};
      const rent = data.rentabilite || {};
      const credit = data.credit_25ans || {};
      const isPositif = data.verdict === 'positif';
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="flex items-center gap-1.5 mb-1.5">
            <Badge className={`text-[10px] ${isPositif ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
              {isPositif ? 'CF+' : 'CF-'}
            </Badge>
            <span className="font-medium text-slate-700 truncate">{bien.titre}</span>
          </div>
          <div className="grid grid-cols-3 gap-1.5">
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Brut</div>
              <div className="font-bold text-[#0D47A1]">{rent.rendement_brut?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Net</div>
              <div className="font-bold text-[#00B050]">{rent.rendement_net?.toFixed(1)}%</div>
            </div>
            <div className="bg-white rounded p-1.5 text-center border border-slate-100">
              <div className="text-[10px] text-slate-400">Cash-flow</div>
              <div className={`font-bold ${rent.cash_flow_mensuel >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {rent.cash_flow_mensuel >= 0 ? '+' : ''}{rent.cash_flow_mensuel?.toLocaleString()}€
              </div>
            </div>
          </div>
          <div className="mt-1.5 flex justify-between text-[10px] text-slate-500">
            <span>Loyer: {data.loyer_estime?.toLocaleString()}€/m</span>
            <span>Crédit: {credit.mensualite?.toLocaleString()}€/m</span>
          </div>
          <button
            onClick={() => saveToPortfolio(data)}
            className="mt-1.5 w-full text-[10px] font-medium text-white bg-gradient-to-r from-[#0D47A1] to-[#00B050] rounded py-1 hover:opacity-90 transition-opacity"
            data-testid="floating-save-portfolio"
          >
            Enregistrer dans Portfolio
          </button>
        </div>
      );
    }

    if (msg.skill_used === 'rechercher_biens') {
      const biens = data.biens || [];
      return (
        <div className="mt-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5 text-xs">
          <div className="flex items-center justify-between mb-1.5 cursor-pointer" onClick={() => toggleCard(idx)}>
            <span className="font-medium text-slate-600">{data.nb_resultats} biens à {data.ville}</span>
            {open ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
          </div>
          <div className="space-y-1">
            {biens.slice(0, open ? 6 : 2).map((b, i) => (
              <div key={i} className="bg-white rounded p-1.5 border border-slate-100">
                <div className="flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-medium truncate">{b.titre}</p>
                    <p className="text-[10px] text-slate-400 flex items-center gap-1"><MapPin className="h-2.5 w-2.5" />{b.localisation} {b.surface > 0 && `· ${b.surface}m²`}</p>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0">
                    <div className="font-bold text-rose-600">{b.prix?.toLocaleString()}€</div>
                    {b.url && <a href={b.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-[#0D47A1] flex items-center gap-0.5 justify-end"><ExternalLink className="h-2.5 w-2.5" />Voir</a>}
                  </div>
                </div>
                <button
                  onClick={() => analyzeProperty(b, i)}
                  disabled={loading}
                  className="mt-1 w-full text-[10px] font-medium text-[#0D47A1] border border-[#0D47A1] rounded py-0.5 hover:bg-[#0D47A1] hover:text-white transition-colors disabled:opacity-50"
                  data-testid={`floating-analyze-bien-${i}`}
                >
                  Analyser Bien N°{i + 1}
                </button>
              </div>
            ))}
          </div>
          {biens.length > 2 && !open && (
            <p className="text-[10px] text-center text-slate-400 mt-1 cursor-pointer" onClick={() => toggleCard(idx)}>+ {biens.length - 2} autres...</p>
          )}
        </div>
      );
    }

    return null;
  };

  // Logo component
  const Logo = ({ className }) => (
    <svg viewBox="0 0 100 50" className={className}>
      <text x="5" y="20" fontSize="18" fontWeight="bold" fill="#0D47A1">10M</text>
      <text x="5" y="42" fontSize="20" fontWeight="bold">
        <tspan fill="#00B050">i</tspan>
        <tspan fill="#0D47A1">nvestor</tspan>
      </text>
      <circle cx="8" cy="28" r="3" fill="#00B050" />
    </svg>
  );

  return (
    <>
      <AIConsentModal
        open={needsConsent}
        onAccept={() => {
          setNeedsConsent(false);
          setIsOpen(true);
        }}
        onDecline={() => {
          setNeedsConsent(false);
          setIsOpen(false);
          toast.info('Vous pouvez accepter plus tard pour utiliser l\'IA.');
        }}
      />
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => {
            if (!hasAIConsent()) {
              setNeedsConsent(true);
            } else {
              setIsOpen(true);
            }
          }}
          className="fixed bottom-20 sm:bottom-6 right-4 sm:right-6 z-50 w-14 h-14 sm:w-16 sm:h-16 bg-white 
                     rounded-full shadow-lg hover:shadow-xl transition-all duration-300 
                     flex items-center justify-center group hover:scale-110 border-2 border-slate-200"
          data-testid="floating-chat-button"
        >
          <img 
            src="https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/3n7jizcm_LOGO%2010MILLIONSINVESTOR%20blanc.png" 
            alt="10M investor" 
            className="w-10 h-10 object-contain"
          />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-400 rounded-full border-2 border-white animate-pulse"></span>
        </button>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed z-50 bg-white rounded-2xl shadow-2xl border border-slate-200 
                      flex flex-col overflow-hidden transition-all duration-300
                      ${isExpanded 
                        ? 'bottom-4 right-4 left-4 top-4 md:left-auto md:w-[600px] md:top-20' 
                        : 'bottom-2 right-2 left-2 sm:left-auto sm:bottom-6 sm:right-6 sm:w-96 h-[85vh] sm:h-[520px]'}`}
          data-testid="floating-chat-window"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b bg-gradient-to-r from-[#00B050] to-[#0D47A1]">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center p-1">
                <img 
                  src="https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/3n7jizcm_LOGO%2010MILLIONSINVESTOR%20blanc.png" 
                  alt="10M investor" 
                  className="w-8 h-8 object-contain"
                />
              </div>
              <div>
                <h3 className="text-white font-semibold">Assistant 10M investor</h3>
                <p className="text-xs text-white/80">Expert immobilier IA</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {messages.length > 0 && (
                <Button variant="ghost" size="sm" onClick={clearChat} className="text-white/80 hover:text-white hover:bg-white/20">
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={() => setIsExpanded(!isExpanded)} className="text-white/80 hover:text-white hover:bg-white/20">
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)} className="text-white/80 hover:text-white hover:bg-white/20">
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50">
            {messages.length === 0 && (
              <div className="text-center py-8">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-[#00B050]/10 to-[#0D47A1]/10 flex items-center justify-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_1dae8851-70c6-45a8-a7e5-858edaeb24e4/artifacts/3n7jizcm_LOGO%2010MILLIONSINVESTOR%20blanc.png" 
                    alt="10M investor" 
                    className="w-12 h-12 object-contain"
                  />
                </div>
                <h4 className="text-slate-700 font-medium mb-2">Comment puis-je t'aider ?</h4>
                <p className="text-slate-500 text-sm mb-4">
                  Pose une question ou utilise le micro pour naviguer
                </p>
                <div className="flex flex-wrap justify-center gap-2">
                  {['Rentabilité 180000€, loyer 750€', 'Un bien à 200000€ à Lyon', 'Crédit 250000€ sur 25 ans'].map((q, i) => (
                    <button
                      key={i}
                      onClick={() => setInputValue(q)}
                      className="px-3 py-1.5 text-xs bg-white text-slate-600 rounded-full hover:bg-slate-100 transition-colors border border-slate-200"
                    >
                      {q}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  🎤 Dis "Ouvre le calculateur" pour naviguer
                </p>
              </div>
            )}
            
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[85%] ${msg.role === 'user' ? '' : 'w-full max-w-[85%]'}`}>
                  {msg.skill_used && msg.skill_label && (
                    <div className="flex items-center gap-1 mb-1 ml-1">
                      {skillIcons[msg.skill_used]}
                      <span className="text-[10px] font-medium text-[#0D47A1] bg-[#0D47A1]/10 px-1.5 py-0.5 rounded-full">
                        {msg.skill_label}
                      </span>
                    </div>
                  )}
                  <div
                    className={`rounded-2xl px-4 py-2.5 ${
                      msg.role === 'user'
                        ? 'bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white'
                        : 'bg-white text-slate-700 border border-slate-200 shadow-sm'
                    }`}
                  >
                    {msg.has_pdf && <FileText className="w-4 h-4 inline mr-2" />}
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                  </div>
                  {renderMiniSkillCard(msg, idx)}
                </div>
              </div>
            ))}
            
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white rounded-2xl px-4 py-3 border border-slate-200 shadow-sm">
                  <Loader2 className="w-5 h-5 text-[#0D47A1] animate-spin" />
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex items-center gap-2">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                accept=".pdf"
                className="hidden"
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={loading}
                className="text-slate-500 hover:text-[#0D47A1] border-slate-200"
              >
                <Upload className="w-4 h-4" />
              </Button>
              <Button
                variant={isListening ? "default" : "outline"}
                size="sm"
                onClick={toggleListening}
                disabled={loading}
                className={isListening 
                  ? "bg-red-500 hover:bg-red-600 text-white animate-pulse" 
                  : "text-slate-500 hover:text-[#00B050] border-slate-200"
                }
              >
                {isListening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
              </Button>
              <Input
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={isListening ? "🎤 Je t'écoute..." : "Pose ta question..."}
                disabled={loading}
                className="flex-1 border-slate-200 focus:border-[#0D47A1] focus:ring-[#0D47A1]"
              />
              <Button
                onClick={handleSend}
                disabled={!inputValue.trim() || loading}
                className="bg-gradient-to-r from-[#00B050] to-[#0D47A1] hover:opacity-90"
                data-testid="floating-send-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FloatingAIChat;
